import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, absoluteApiFilePreviewUrl } from '../lib/api'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { useToast } from '../ui/ToastProvider'
import MenuGuideButton from '../components/MenuGuideButton'
import { buildClaimFaxRoutingPayload, parseClaimFaxRoutingFields } from '../lib/claimFaxRouting'
import { INSURER_SYSTEMS_SECTIONS } from './insurerSystemsData'
import {
  findInsurerSystemItem,
  flattenInsurerSystemItems,
  linkItemsFromText,
  linkTextValue,
} from '../lib/insurerSystemProfileUtils'
import ClaimLogoEditor from '../components/insurerMaster/ClaimLogoEditor'
import InsurerFaxSettingCells from '../components/insurerMaster/InsurerFaxSettingCells'

const TABS = [
  { key: 'master', label: '마스터 기본 데이터' },
  { key: 'claim', label: '청구 관련' },
  { key: 'system', label: '전산 관련' },
]

const ORG_LINK_FILTERS = [
  { key: 'all', label: '전체' },
  { key: 'linked', label: '연결된 것만' },
  { key: 'unlinked', label: '연결 안 된 것만' },
]

function unwrapPayload(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res?.data
}

function digitsKey(s) {
  return String(s || '').replace(/\D/g, '')
}

function emptyFaxDraft() {
  return {
    claimFaxNumber: '',
    virtualFaxRequired: false,
    memo: '',
    faxRoutingDisease: '',
    faxRoutingInjury: '',
  }
}

function customerRegistrationAutomation(row) {
  return row?.customerRegistrationAutomation || {}
}

function customerRegistrationAutomationLabel(row) {
  if (!row?.orgId) return 'org 연결 필요'
  const automation = customerRegistrationAutomation(row)
  if (!automation.configured) return '미설정'
  if (automation.requiresIdAuth || automation.requiresLoginMobileAuth) return '사람 인증 필요'
  if (automation.requiresArsPrereg || automation.supportStatus === 'ARS_PREREG_SUPPORTED') return 'ARS 후 진행'
  if (automation.supportStatus === 'SUPPORTED') return '자동화 가능'
  if (automation.supportStatus === 'SEMI_SUPPORTED') return '부분 지원'
  if (automation.supportStatus === 'EXCLUDED') return '자동화 제외'
  return '검토 필요'
}

function customerRegistrationAutomationTone(row) {
  const label = customerRegistrationAutomationLabel(row)
  if (label === '자동화 가능') return 'bg-emerald-50 text-emerald-700'
  if (label === '부분 지원') return 'bg-sky-50 text-sky-700'
  if (label === 'ARS 후 진행') return 'bg-amber-50 text-amber-700'
  if (label === '사람 인증 필요' || label === '자동화 제외') return 'bg-rose-50 text-rose-700'
  return 'bg-slate-100 text-slate-600'
}

function firstHref(items) {
  const arr = Array.isArray(items) ? items : []
  const found = arr.find((x) => x?.href)
  return found?.href || ''
}

function orgCategoryLabel(org, lifeRootId, nonlifeRootId) {
  if (lifeRootId != null && Number(org?.parentOrgId) === lifeRootId) return '생명'
  if (nonlifeRootId != null && Number(org?.parentOrgId) === nonlifeRootId) return '손해'
  return '-'
}

export default function InsurersMasterAdminPage() {
  const { show } = useToast()
  const navigate = useNavigate()
  const { id: routeInsurerId } = useParams()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [items, setItems] = useState([])
  const [orgOptions, setOrgOptions] = useState([])
  const [faxRows, setFaxRows] = useState({})
  const [systemProfiles, setSystemProfiles] = useState({})
  const [q, setQ] = useState('')
  const [orgLinkFilter, setOrgLinkFilter] = useState('all')
  const [activeTab, setActiveTab] = useState('master')

  const [masterDrafts, setMasterDrafts] = useState({})
  const [systemDrafts, setSystemDrafts] = useState({})
  const [savingMasterId, setSavingMasterId] = useState(null)
  const [savingFaxId, setSavingFaxId] = useState(null)
  const [savingSystemId, setSavingSystemId] = useState(null)
  const [importingSystemProfiles, setImportingSystemProfiles] = useState(false)
  const [logoBusyId, setLogoBusyId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [insRes, orgRes, faxRes, systemRes] = await Promise.all([
        api.get('/operator/insurers'),
        api.get('/orgs/options', { params: { scope: 'all' } }),
        api.get('/claims/insurer-fax-settings'),
        api.get('/operator/insurer-system-profiles'),
      ])

      const insPayload = unwrapPayload(insRes)
      const orgPayload = unwrapPayload(orgRes)
      const faxPayload = unwrapPayload(faxRes)
      const systemPayload = unwrapPayload(systemRes)
      const ins = Array.isArray(insPayload) ? insPayload : (insPayload?.items || [])
      const faxMap = {}
      const systemMap = {}

      for (const row of Array.isArray(faxPayload) ? faxPayload : []) {
        const id = row?.insurerOrgId
        if (id == null) continue
        const { faxRoutingDisease, faxRoutingInjury } = parseClaimFaxRoutingFields(row.claimFaxRoutingJson)
        faxMap[String(id)] = {
          claimFaxNumber: row.claimFaxNumber || '',
          virtualFaxRequired: !!row.virtualFaxRequired,
          memo: row.memo || '',
          faxRoutingDisease,
          faxRoutingInjury,
        }
      }

      const systemList = Array.isArray(systemPayload) ? systemPayload : (systemPayload?.items || [])
      for (const row of Array.isArray(systemList) ? systemList : []) {
        if (row?.insurerId == null) continue
        systemMap[String(row.insurerId)] = row
      }

      setItems(Array.isArray(ins) ? ins : [])
      setOrgOptions(Array.isArray(orgPayload) ? orgPayload : [])
      setFaxRows(faxMap)
      setSystemProfiles(systemMap)
      setMasterDrafts({})
      setSystemDrafts({})
    } catch (e) {
      setError('원수사(마스터) 목록을 불러오지 못했습니다')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const orgById = useMemo(() => {
    const m = new Map()
    for (const o of Array.isArray(orgOptions) ? orgOptions : []) {
      if (o?.id != null) m.set(Number(o.id), o)
    }
    return m
  }, [orgOptions])

  const insurerOrgOptions = useMemo(() => {
    const arr = Array.isArray(orgOptions) ? orgOptions : []
    const roots = arr.filter((o) => {
      if (!o || String(o?.type) !== 'INSURER' || o?.parentOrgId != null) return false
      const n = String(o?.name || '')
      return n.includes('생명') || n.includes('생보') || n.includes('손해') || n.includes('화재') || n.includes('해상')
    })
    const rootIds = new Set(roots.map((o) => String(o.id)))

    return arr
      .filter((o) => o && String(o?.type) === 'INSURER' && o?.parentOrgId != null && rootIds.has(String(o.parentOrgId)))
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'ko'))
  }, [orgOptions])

  const lifeRootId = useMemo(() => {
    const root = (orgOptions || []).find((o) => o?.type === 'INSURER' && !o?.parentOrgId && String(o?.name || '').includes('생명'))
    return root?.id != null ? Number(root.id) : null
  }, [orgOptions])

  const nonlifeRootId = useMemo(() => {
    const root = (orgOptions || []).find((o) =>
      o?.type === 'INSURER'
      && !o?.parentOrgId
      && (String(o?.name || '').includes('손해') || String(o?.name || '').includes('화재') || String(o?.name || '').includes('해상')),
    )
    return root?.id != null ? Number(root.id) : null
  }, [orgOptions])

  const systemItems = useMemo(() => flattenInsurerSystemItems(INSURER_SYSTEMS_SECTIONS), [])

  const insurerRows = useMemo(() => {
    const qq = String(q || '').trim().toLowerCase()
    const arr = Array.isArray(items) ? items : []
    const mapped = arr.map((x) => {
      const org = x?.orgId != null ? orgById.get(Number(x.orgId)) : null
      const displayName = x?.displayName || x?.orgName || org?.name || x?.name || x?.code
      const systemProfile = systemProfiles[String(x?.id)] || null
      return {
        ...x,
        displayName,
        org,
        systemProfile,
        systemItem: systemProfile || findInsurerSystemItem({ ...x, displayName, orgName: x?.orgName || org?.name }, systemItems),
      }
    })
    const searched = !qq
      ? mapped
      : mapped.filter((x) => {
        const hay = `${x?.code || ''} ${x?.name || ''} ${x?.orgName || ''} ${x?.displayName || ''}`.toLowerCase()
        return hay.includes(qq)
      })
    const filtered = orgLinkFilter === 'linked'
      ? searched.filter((x) => x?.orgId != null)
      : orgLinkFilter === 'unlinked'
        ? searched.filter((x) => x?.orgId == null)
        : searched
    return filtered.sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || ''), 'ko'))
  }, [items, orgById, orgLinkFilter, q, systemItems, systemProfiles])

  const tabCounts = useMemo(() => {
    const linked = insurerRows.filter((row) => row?.orgId != null).length
    const systemMatched = insurerRows.filter((row) => row?.systemItem).length
    const customerRegistrationReady = insurerRows.filter((row) => {
      const automation = customerRegistrationAutomation(row)
      return automation.configured && ['SUPPORTED', 'SEMI_SUPPORTED', 'ARS_PREREG_SUPPORTED'].includes(automation.supportStatus)
    }).length
    return { total: insurerRows.length, linked, systemMatched, customerRegistrationReady }
  }, [insurerRows])

  const setMasterDraft = (rowId, patch) => {
    setMasterDrafts((prev) => ({
      ...prev,
      [String(rowId)]: { ...(prev[String(rowId)] || {}), ...patch },
    }))
  }

  const systemValue = (row, key) => {
    const draft = systemDrafts[String(row.id)] || {}
    if (draft[key] !== undefined) return draft[key]
    const item = row.systemItem || {}
    if (key === 'sectionId') return item.sectionId || 'fire'
    if (key === 'sectionTitle') return item.sectionTitle || item.section || '손해보험사'
    if (key === 'firstColLabel') return item.firstColLabel || '보험회사 전산'
    if (key === 'active') return item.active !== false
    if (key === 'customerCenterText') return linkTextValue(item.customerCenter)
    if (key === 'inCallText') return linkTextValue(item.inCall)
    if (key === 'helpDeskText') return linkTextValue(item.helpDesk)
    if (key === 'claimFaxText') return linkTextValue(item.claimFax)
    return item[key] || ''
  }

  const setSystemDraft = (rowId, patch) => {
    setSystemDrafts((prev) => ({
      ...prev,
      [String(rowId)]: { ...(prev[String(rowId)] || {}), ...patch },
    }))
  }

  const saveSystemProfile = async (row) => {
    if (!row?.id) return
    const payload = buildSystemProfilePayload(row)
    if (!payload.sectionId || !payload.sectionTitle) {
      show('전산 구분과 구분명은 필수입니다.', 'error')
      return
    }
    setSavingSystemId(String(row.id))
    try {
      await api.put(`/operator/insurer-system-profiles/${row.id}`, payload)
      show('전산 정보를 저장했습니다.', 'success')
      await load()
    } catch (e) {
      show(e?.response?.data?.message || '전산 정보 저장 실패', 'error')
    } finally {
      setSavingSystemId(null)
    }
  }

  const buildSystemProfilePayload = (row) => ({
    sectionId: String(systemValue(row, 'sectionId') || '').trim(),
    sectionTitle: String(systemValue(row, 'sectionTitle') || '').trim(),
    firstColLabel: systemValue(row, 'firstColLabel') || null,
    systemUrl: systemValue(row, 'systemUrl') || null,
    termsUrl: systemValue(row, 'termsUrl') || null,
    formUrl: systemValue(row, 'formUrl') || null,
    logoUrl: systemValue(row, 'logoUrl') || null,
    customerCenter: linkItemsFromText(systemValue(row, 'customerCenterText')),
    inCall: linkItemsFromText(systemValue(row, 'inCallText')),
    helpDesk: linkItemsFromText(systemValue(row, 'helpDeskText')),
    claimFax: linkItemsFromText(systemValue(row, 'claimFaxText')),
    sortOrder: row.sortOrder ?? null,
    active: !!systemValue(row, 'active'),
  })

  const deleteSystemProfile = async (row) => {
    if (!row?.id || !row.systemProfile) return
    if (!window.confirm('DB 전산 프로필 관리를 해제할까요? 기존 정적값 fallback으로 돌아갑니다.')) return
    setSavingSystemId(String(row.id))
    try {
      await api.delete(`/operator/insurer-system-profiles/${row.id}`)
      show('DB 전산 프로필을 해제했습니다.', 'success')
      await load()
    } catch (e) {
      show(e?.response?.data?.message || 'DB 관리 해제 실패', 'error')
    } finally {
      setSavingSystemId(null)
    }
  }

  const importStaticSystemProfiles = async () => {
    const targets = insurerRows.filter((row) => row.orgId && row.systemItem && !row.systemProfile)
    if (targets.length === 0) {
      show('DB로 저장할 정적 fallback 항목이 없습니다.', 'info')
      return
    }
    if (!window.confirm(`정적 fallback ${targets.length}건을 DB 전산 프로필로 저장할까요?`)) return
    setImportingSystemProfiles(true)
    try {
      for (const row of targets) {
        await api.put(`/operator/insurer-system-profiles/${row.id}`, buildSystemProfilePayload(row))
      }
      show(`전산 프로필 ${targets.length}건을 DB로 저장했습니다.`, 'success')
      await load()
    } catch (e) {
      show(e?.response?.data?.message || '일괄 저장 중 오류가 발생했습니다.', 'error')
    } finally {
      setImportingSystemProfiles(false)
    }
  }

  const masterValue = (row, key) => {
    const draft = masterDrafts[String(row.id)] || {}
    if (draft[key] !== undefined) return draft[key]
    if (key === 'active') return row?.active !== false
    if (key === 'orgId') return row?.orgId != null ? String(row.orgId) : ''
    return row?.[key] || ''
  }

  const hasMasterChanges = (row) => {
    const draft = masterDrafts[String(row.id)] || {}
    if (draft.name !== undefined && String(draft.name || '') !== String(row?.name || '')) return true
    if (draft.orgId !== undefined && String(draft.orgId || '') !== (row?.orgId != null ? String(row.orgId) : '')) return true
    if (draft.active !== undefined && !!draft.active !== (row?.active !== false)) return true
    return false
  }

  const saveMaster = async (row) => {
    if (!row?.id) return
    const name = String(masterValue(row, 'name') || '').trim()
    if (!name) {
      show('NAME은 비어 있을 수 없습니다', 'error')
      return
    }
    const orgIdRaw = masterValue(row, 'orgId')
    setSavingMasterId(String(row.id))
    try {
      await api.patch(`/operator/insurers/${row.id}`, {
        name,
        orgId: orgIdRaw ? Number(orgIdRaw) : null,
        active: !!masterValue(row, 'active'),
      })
      show('마스터 기본 데이터를 저장했습니다.', 'success')
      await load()
    } catch (e) {
      show(e?.response?.data?.message || '저장 실패', 'error')
    } finally {
      setSavingMasterId(null)
    }
  }

  const getFaxDraft = (orgId) => faxRows[String(orgId)] || emptyFaxDraft()

  const setFaxDraft = (orgId, patch) => {
    setFaxRows((prev) => ({
      ...prev,
      [String(orgId)]: { ...(prev[String(orgId)] || emptyFaxDraft()), ...patch },
    }))
  }

  const saveFax = async (orgId) => {
    if (!orgId) return
    const r = getFaxDraft(orgId)
    const hasRef =
      !!digitsKey(r.claimFaxNumber) || !!digitsKey(r.faxRoutingDisease) || !!digitsKey(r.faxRoutingInjury)
    if (r.virtualFaxRequired && !hasRef) {
      show('가상팩스 필수 보험사는 기본/질병/상해 안내 번호 중 하나 이상을 입력해야 합니다.', 'error')
      return
    }
    setSavingFaxId(String(orgId))
    try {
      await api.put(`/claims/insurer-fax-settings/${orgId}`, {
        claimFaxNumber: r.claimFaxNumber?.trim() || null,
        virtualFaxRequired: !!r.virtualFaxRequired,
        memo: r.memo?.trim() || null,
        claimFaxRoutingJson: buildClaimFaxRoutingPayload(r.faxRoutingDisease, r.faxRoutingInjury),
      })
      show('청구번호를 저장했습니다.', 'success')
      await load()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error?.message || ''
      show(msg || '청구번호 저장에 실패했습니다.', 'error')
    } finally {
      setSavingFaxId(null)
    }
  }

  const uploadClaimLogo = async (row, file) => {
    if (!row?.orgId || !file) return
    if (file.size > 10 * 1024 * 1024) {
      show('이미지는 10MB 이하여야 합니다.', 'error')
      return
    }
    setLogoBusyId(String(row.orgId))
    try {
      const body = new FormData()
      body.append('file', file)
      const res = await api.post('/files/upload/claim-brand-logo-image', body)
      const fileId = res?.data?.data?.fileId || res?.data?.fileId || ''
      if (!fileId) {
        show('파일 ID를 받지 못했습니다.', 'error')
        return
      }
      await api.patch(`/orgs/${row.orgId}/claim-brand-logo`, { claimBrandLogoFileId: fileId })
      show('청구 로고를 저장했습니다.', 'success')
      await load()
    } catch (e) {
      show(e?.response?.data?.message || e?.message || '청구 로고 저장 실패', 'error')
    } finally {
      setLogoBusyId(null)
    }
  }

  const clearClaimLogo = async (row) => {
    if (!row?.orgId) return
    if (!window.confirm('청구 로고를 해제할까요?')) return
    setLogoBusyId(String(row.orgId))
    try {
      await api.patch(`/orgs/${row.orgId}/claim-brand-logo`, { claimBrandLogoFileId: null })
      show('청구 로고를 해제했습니다.', 'success')
      await load()
    } catch (e) {
      show(e?.response?.data?.message || e?.message || '청구 로고 해제 실패', 'error')
    } finally {
      setLogoBusyId(null)
    }
  }

  if (loading && insurerRows.length === 0) return <Loading />
  if (error) return <InlineError message={error} />

  const routeDetailRow = routeInsurerId
    ? insurerRows.find((row) => String(row.id) === String(routeInsurerId))
    : null

  if (routeInsurerId) {
    if (!routeDetailRow) return <InlineError message="원수사를 찾을 수 없습니다" />
    return (
      <DetailPage
        row={routeDetailRow}
        orgById={orgById}
        insurerOrgOptions={insurerOrgOptions}
        lifeRootId={lifeRootId}
        nonlifeRootId={nonlifeRootId}
        faxDraft={routeDetailRow.orgId ? getFaxDraft(routeDetailRow.orgId) : emptyFaxDraft()}
        masterValue={masterValue}
        setMasterDraft={setMasterDraft}
        saveMaster={saveMaster}
        savingMaster={savingMasterId === String(routeDetailRow.id)}
        setFaxDraft={setFaxDraft}
        saveFax={saveFax}
        savingFax={savingFaxId === String(routeDetailRow.orgId)}
        uploadClaimLogo={uploadClaimLogo}
        clearClaimLogo={clearClaimLogo}
        logoBusy={logoBusyId === String(routeDetailRow.orgId)}
        systemValue={systemValue}
        setSystemDraft={setSystemDraft}
        saveSystemProfile={saveSystemProfile}
        deleteSystemProfile={deleteSystemProfile}
        savingSystem={savingSystemId === String(routeDetailRow.id)}
        back={() => navigate('/data/insurers')}
      />
    )
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">원수사(마스터) 관리</h1>
            <MenuGuideButton slug="data-management" title="데이터관리 가이드" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link className="rounded border px-3 py-2 text-sm hover:bg-slate-50" to="/orgs">
            조직관리
          </Link>
          <Link className="rounded border px-3 py-2 text-sm hover:bg-slate-50" to="/ops/forms/claims/fax">
            청구번호 관리
          </Link>
          <Link className="rounded border px-3 py-2 text-sm hover:bg-slate-50" to="/work/insurer-systems">
            보험사전산
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          className="w-full max-w-md rounded border px-3 py-2 text-sm"
          placeholder="검색: code / name / orgName"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="org 연결 필터">
          {ORG_LINK_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`rounded-full border px-3 py-1.5 text-sm font-medium ${
                orgLinkFilter === f.key
                  ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
              onClick={() => setOrgLinkFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={load} disabled={loading}>
          {loading ? '로딩…' : '새로고침'}
        </button>
        {activeTab === 'system' ? (
          <button
            type="button"
            className="rounded bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            onClick={importStaticSystemProfiles}
            disabled={loading || importingSystemProfiles}
          >
            {importingSystemProfiles ? '저장 중…' : '정적값 전체 DB 저장'}
          </button>
        ) : null}
        <span className="text-sm text-slate-500">
          총 {tabCounts.total}개 · org 연결 {tabCounts.linked}개 · 고객등록 가능 {tabCounts.customerRegistrationReady}개 · 전산 매칭 {tabCounts.systemMatched}개
        </span>
      </div>

      <div className="flex flex-wrap gap-2 border-b">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`-mb-px rounded-t border px-4 py-2 text-sm font-medium ${
              activeTab === tab.key
                ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
                : 'border-transparent text-slate-600 hover:bg-slate-50'
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded border bg-white">
        <div className="overflow-auto">
          <table className="min-w-[1320px] w-full text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="sticky left-0 z-20 w-[260px] border-r bg-slate-50 px-4 py-2 text-left">원수사</th>
                {activeTab === 'master' && (
                  <>
                    <th className="px-3 py-2 text-left w-[90px]">사용</th>
                    <th className="px-3 py-2 text-left w-[220px]">NAME</th>
                    <th className="px-3 py-2 text-left w-[300px]">연결 org</th>
                    <th className="px-3 py-2 text-left w-[220px]">고객등록 가능 여부</th>
                    <th className="px-3 py-2 text-left">상태</th>
                    <th className="px-3 py-2 text-left w-[110px]"></th>
                  </>
                )}
                {activeTab === 'claim' && (
                  <>
                    <th className="px-3 py-2 text-left w-[220px]">청구 로고</th>
                    <th className="px-3 py-2 text-left w-[170px]">기본 안내 팩스</th>
                    <th className="px-3 py-2 text-left w-[150px]">질병 안내 팩스</th>
                    <th className="px-3 py-2 text-left w-[150px]">상해계 안내 팩스</th>
                    <th className="px-3 py-2 text-left w-[120px]">가상팩스</th>
                    <th className="px-3 py-2 text-left">메모</th>
                    <th className="px-3 py-2 text-left w-[110px]"></th>
                  </>
                )}
                {activeTab === 'system' && (
                  <>
                    <th className="px-3 py-2 text-left w-[130px]">구분</th>
                    <th className="px-3 py-2 text-left w-[220px]">전산 URL</th>
                    <th className="px-3 py-2 text-left w-[180px]">고객센터</th>
                    <th className="px-3 py-2 text-left w-[180px]">헬프데스크</th>
                    <th className="px-3 py-2 text-left w-[180px]">전산표 청구팩스</th>
                    <th className="px-3 py-2 text-left">약관/서식</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {insurerRows.map((row) => (
                <tr key={row.id} className="border-t align-top hover:bg-slate-50/60">
                  <td className="sticky left-0 z-10 border-r bg-white px-4 py-3">
                    <div className="flex items-start gap-3">
                      <LogoThumb row={row} />
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900">{row.displayName || '-'}</div>
                        <div className="mt-0.5 text-xs text-slate-500">{row.code}</div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          <span className={`rounded-full px-2 py-0.5 text-[11px] ${row.active === false ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-700'}`}>
                            {row.active === false ? 'OFF' : 'ON'}
                          </span>
                          {row.orgId ? (
                            <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] text-brand-navy">org 연결</span>
                          ) : (
                            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">org 연결 필요</span>
                          )}
                          <span className={`rounded-full px-2 py-0.5 text-[11px] ${customerRegistrationAutomationTone(row)}`}>
                            고객등록 {customerRegistrationAutomationLabel(row)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>

                  {activeTab === 'master' && (
                    <MasterCells
                      row={row}
                      insurerOrgOptions={insurerOrgOptions}
                      saving={savingMasterId === String(row.id)}
                      masterValue={masterValue}
                      hasChanges={hasMasterChanges(row)}
                      setMasterDraft={setMasterDraft}
                      saveMaster={saveMaster}
                      openDetail={(row) => navigate(`/data/insurers/${row.id}`)}
                    />
                  )}
                  {activeTab === 'claim' && (
                    <ClaimCells
                      row={row}
                      faxDraft={row.orgId ? getFaxDraft(row.orgId) : emptyFaxDraft()}
                      saving={savingFaxId === String(row.orgId)}
                      logoBusy={logoBusyId === String(row.orgId)}
                      setFaxDraft={setFaxDraft}
                      saveFax={saveFax}
                      uploadClaimLogo={uploadClaimLogo}
                      clearClaimLogo={clearClaimLogo}
                    />
                  )}
                  {activeTab === 'system' && (
                    <SystemCells
                      row={row}
                      systemValue={systemValue}
                      setSystemDraft={setSystemDraft}
                      saveSystemProfile={saveSystemProfile}
                      deleteSystemProfile={deleteSystemProfile}
                      saving={savingSystemId === String(row.id)}
                    />
                  )}
                </tr>
              ))}
              {insurerRows.length === 0 && (
                <tr>
                  <td className="px-4 py-8 text-center text-slate-500" colSpan={8}>
                    표시할 항목이 없습니다
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </section>
  )
}

function LogoThumb({ row }) {
  const fid = row?.org?.claimBrandLogoFileId || row?.org?.logoFileId
  if (!fid) {
    return (
      <div className="flex h-10 w-16 shrink-0 items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50 text-[11px] text-slate-400">
        로고
      </div>
    )
  }
  return (
    <img
      src={absoluteApiFilePreviewUrl(fid)}
      alt=""
      className="h-10 w-16 shrink-0 rounded border border-slate-200 bg-white object-contain p-0.5"
      loading="lazy"
    />
  )
}

function logoPreviewUrl(row) {
  const fid = row?.org?.claimBrandLogoFileId || row?.org?.logoFileId
  return fid ? absoluteApiFilePreviewUrl(fid) : ''
}

function MasterCells({
  row,
  insurerOrgOptions,
  saving,
  masterValue,
  hasChanges,
  setMasterDraft,
  saveMaster,
  openDetail,
}) {
  return (
    <>
      <td className="px-3 py-3">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!masterValue(row, 'active')}
            onChange={(e) => setMasterDraft(row.id, { active: e.target.checked })}
            disabled={saving}
          />
          <span>{masterValue(row, 'active') ? 'ON' : 'OFF'}</span>
        </label>
      </td>
      <td className="px-3 py-3">
        <input
          className="w-full rounded border px-2 py-1.5"
          value={masterValue(row, 'name')}
          onChange={(e) => setMasterDraft(row.id, { name: e.target.value })}
          disabled={saving}
        />
      </td>
      <td className="px-3 py-3">
        <select
          className="w-full rounded border px-2 py-1.5"
          value={masterValue(row, 'orgId')}
          onChange={(e) => setMasterDraft(row.id, { orgId: e.target.value })}
          disabled={saving}
        >
          <option value="">연결 안 함</option>
          {insurerOrgOptions.map((o) => (
            <option key={o.id} value={String(o.id)}>{o.name}</option>
          ))}
        </select>
      </td>
      <td className="px-3 py-3">
        <CustomerRegistrationAutomationCell row={row} />
      </td>
      <td className="px-3 py-3">
        {row.orgId ? (
          <div className="space-y-1">
            <div className="font-medium text-slate-800">{row.orgName || row.org?.name || '-'}</div>
            <Link className="text-xs text-brand-navy underline" to={`/orgs?openOrg=${row.orgId}`}>
              조직관리에서 열기
            </Link>
          </div>
        ) : (
          <span className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-700">청구/전산 탭 일부 비활성</span>
        )}
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded bg-brand-navy px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            onClick={() => saveMaster(row)}
            disabled={saving || !hasChanges}
          >
            {saving ? '저장…' : '저장'}
          </button>
          <button
            type="button"
            className="rounded border px-3 py-1.5 text-xs hover:bg-slate-50"
            onClick={() => openDetail(row)}
          >
            상세
          </button>
        </div>
      </td>
    </>
  )
}

function CustomerRegistrationAutomationCell({ row }) {
  const automation = customerRegistrationAutomation(row)
  return (
    <div className="space-y-1">
      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${customerRegistrationAutomationTone(row)}`}>
        {customerRegistrationAutomationLabel(row)}
      </span>
      {automation.profileCode ? (
        <div className="break-all text-xs text-slate-500">{automation.profileCode}</div>
      ) : null}
      {automation.notes ? (
        <div className="line-clamp-2 text-xs text-slate-500">{automation.notes}</div>
      ) : null}
    </div>
  )
}

function ClaimCells({
  row,
  faxDraft,
  saving,
  logoBusy,
  setFaxDraft,
  saveFax,
  uploadClaimLogo,
  clearClaimLogo,
}) {
  const disabled = !row.orgId
  return (
    <>
      <td className="px-3 py-3">
        {disabled ? (
          <OrgRequired />
        ) : (
          <ClaimLogoEditor
            compact
            preview={logoPreviewUrl(row)}
            hasClaimLogo={!!row?.org?.claimBrandLogoFileId}
            busy={logoBusy}
            onUpload={(file) => uploadClaimLogo(row, file)}
            onClear={() => clearClaimLogo(row)}
          />
        )}
      </td>
      <InsurerFaxSettingCells
        value={faxDraft}
        disabled={disabled}
        saving={saving}
        onChange={(patch) => setFaxDraft(row.orgId, patch)}
        onSave={() => saveFax(row.orgId)}
      />
    </>
  )
}

function SystemCells({ row, systemValue, setSystemDraft, saveSystemProfile, deleteSystemProfile, saving }) {
  if (!row.orgId) {
    return (
      <td className="px-3 py-3" colSpan={6}>
        <OrgRequired />
      </td>
    )
  }

  return (
    <>
      <td className="px-3 py-3">
        <div className="mb-2">
          <SystemSourceBadge row={row} />
        </div>
        <div className="flex gap-2">
          <select
            className="w-24 rounded border px-2 py-1.5"
            value={systemValue(row, 'sectionId')}
            onChange={(e) => {
              const sectionId = e.target.value
              const sectionTitle = sectionId === 'life' ? '생명보험사' : sectionId === 'mutual' ? '공제회사' : '손해보험사'
              const firstColLabel = sectionId === 'mutual' ? '공제회사 홈페이지' : '보험회사 전산'
              setSystemDraft(row.id, { sectionId, sectionTitle, firstColLabel })
            }}
            disabled={saving}
          >
            <option value="fire">손해</option>
            <option value="life">생명</option>
            <option value="mutual">공제</option>
          </select>
          <input
            className="w-full rounded border px-2 py-1.5"
            value={systemValue(row, 'sectionTitle')}
            onChange={(e) => setSystemDraft(row.id, { sectionTitle: e.target.value })}
            disabled={saving}
          />
        </div>
      </td>
      <td className="px-3 py-3">
        <input
          className="w-full rounded border px-2 py-1.5"
          value={systemValue(row, 'systemUrl')}
          onChange={(e) => setSystemDraft(row.id, { systemUrl: e.target.value })}
          disabled={saving}
        />
      </td>
      <td className="px-3 py-3">
        <textarea
          className="h-16 w-full rounded border px-2 py-1.5"
          value={systemValue(row, 'customerCenterText')}
          onChange={(e) => setSystemDraft(row.id, { customerCenterText: e.target.value })}
          disabled={saving}
        />
      </td>
      <td className="px-3 py-3">
        <textarea
          className="h-16 w-full rounded border px-2 py-1.5"
          value={systemValue(row, 'helpDeskText')}
          onChange={(e) => setSystemDraft(row.id, { helpDeskText: e.target.value })}
          disabled={saving}
        />
      </td>
      <td className="px-3 py-3">
        <textarea
          className="h-16 w-full rounded border px-2 py-1.5"
          value={systemValue(row, 'claimFaxText')}
          onChange={(e) => setSystemDraft(row.id, { claimFaxText: e.target.value })}
          disabled={saving}
        />
      </td>
      <td className="px-3 py-3">
        <div className="space-y-2">
          <input
            className="w-full rounded border px-2 py-1.5"
            placeholder="약관 URL"
            value={systemValue(row, 'termsUrl')}
            onChange={(e) => setSystemDraft(row.id, { termsUrl: e.target.value })}
            disabled={saving}
          />
          <input
            className="w-full rounded border px-2 py-1.5"
            placeholder="서식 URL"
            value={systemValue(row, 'formUrl')}
            onChange={(e) => setSystemDraft(row.id, { formUrl: e.target.value })}
            disabled={saving}
          />
          <button
            type="button"
            className="rounded bg-brand-navy px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            onClick={() => saveSystemProfile(row)}
            disabled={saving}
          >
            {saving ? '저장…' : row.systemProfile ? '저장' : '정적값 DB 저장'}
          </button>
          {row.systemProfile ? (
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={!!systemValue(row, 'active')}
                  onChange={(e) => setSystemDraft(row.id, { active: e.target.checked })}
                  disabled={saving}
                />
                노출
              </label>
              <button
                type="button"
                className="text-xs font-medium text-rose-700 underline disabled:opacity-50"
                onClick={() => deleteSystemProfile(row)}
                disabled={saving}
              >
                DB 관리 해제
              </button>
            </div>
          ) : null}
        </div>
      </td>
    </>
  )
}

function OrgRequired() {
  return (
    <span className="inline-flex rounded bg-slate-100 px-2 py-1 text-xs text-slate-500">
      org 연결 필요
    </span>
  )
}

function SystemSourceBadge({ row }) {
  if (row?.systemProfile) {
    return (
      <span className="inline-flex rounded bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
        DB 관리중
      </span>
    )
  }
  if (row?.systemItem) {
    return (
      <span className="inline-flex rounded bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
        정적값 fallback
      </span>
    )
  }
  return (
    <span className="inline-flex rounded bg-slate-100 px-2 py-1 text-xs text-slate-500">
      전산값 없음
    </span>
  )
}

function DetailPage({
  row,
  orgById,
  insurerOrgOptions,
  lifeRootId,
  nonlifeRootId,
  faxDraft,
  masterValue,
  setMasterDraft,
  saveMaster,
  savingMaster,
  setFaxDraft,
  saveFax,
  savingFax,
  uploadClaimLogo,
  clearClaimLogo,
  logoBusy,
  systemValue,
  setSystemDraft,
  saveSystemProfile,
  deleteSystemProfile,
  savingSystem,
  back,
}) {
  const org = row?.orgId != null ? orgById.get(Number(row.orgId)) : null
  const systemHref = firstHref(row?.systemItem?.customerCenter)

  return (
    <section className="space-y-5">
      <div className="rounded border bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm text-slate-500">원수사 상세</div>
            <div className="text-xl font-semibold">{row.displayName}</div>
            <div className="mt-1 text-xs text-slate-500">{row.code}</div>
          </div>
          <button className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={back}>
            목록으로
          </button>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          <div className="space-y-3 rounded border p-4">
            <div className="font-semibold">마스터 기본 데이터</div>
            <label className="block text-sm">
              <span className="font-medium text-slate-700">NAME</span>
              <input
                className="mt-1 w-full rounded border px-3 py-2"
                value={masterValue(row, 'name')}
                onChange={(e) => setMasterDraft(row.id, { name: e.target.value })}
                disabled={savingMaster}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700">연결 org</span>
              <select
                className="mt-1 w-full rounded border px-3 py-2"
                value={masterValue(row, 'orgId')}
                onChange={(e) => setMasterDraft(row.id, { orgId: e.target.value })}
                disabled={savingMaster}
              >
                <option value="">연결 안 함</option>
                {insurerOrgOptions.map((o) => (
                  <option key={o.id} value={String(o.id)}>{o.name}</option>
                ))}
              </select>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!masterValue(row, 'active')}
                onChange={(e) => setMasterDraft(row.id, { active: e.target.checked })}
                disabled={savingMaster}
              />
              <span>사용</span>
            </label>
            <div className="rounded border bg-slate-50 p-3">
              <div className="mb-2 text-sm font-semibold text-slate-700">고객등록 가능 여부</div>
              <CustomerRegistrationAutomationCell row={row} />
            </div>
            <div>
              <button
                type="button"
                className="rounded bg-brand-navy px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                onClick={() => saveMaster(row)}
                disabled={savingMaster}
              >
                {savingMaster ? '저장 중…' : '마스터 저장'}
              </button>
            </div>
          </div>

          <div className="space-y-3 rounded border p-4">
            <div className="font-semibold">조직/로고</div>
            {org ? (
              <>
                <div className="text-sm text-slate-700">
                  {org.name} · {orgCategoryLabel(org, lifeRootId, nonlifeRootId)}
                </div>
                <ClaimLogoEditor
                  preview={logoPreviewUrl({ ...row, org })}
                  hasClaimLogo={!!org.claimBrandLogoFileId}
                  busy={logoBusy}
                  onUpload={(file) => uploadClaimLogo(row, file)}
                  onClear={() => clearClaimLogo(row)}
                />
                <Link className="text-sm text-brand-navy underline" to={`/orgs?openOrg=${row.orgId}`}>
                  조직관리에서 열기
                </Link>
              </>
            ) : (
              <OrgRequired />
            )}
          </div>

          <div className="space-y-3 rounded border p-4">
            <div className="font-semibold">청구번호</div>
            {row.orgId ? (
              <>
                {[
                  ['claimFaxNumber', '기본 안내 팩스'],
                  ['faxRoutingDisease', '질병 안내 팩스'],
                  ['faxRoutingInjury', '상해계 안내 팩스'],
                ].map(([key, label]) => (
                  <label key={key} className="block text-sm">
                    <span className="font-medium text-slate-700">{label}</span>
                    <input
                      className="mt-1 w-full rounded border px-3 py-2"
                      value={faxDraft[key]}
                      onChange={(e) => setFaxDraft(row.orgId, { [key]: e.target.value })}
                      disabled={savingFax}
                    />
                  </label>
                ))}
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!faxDraft.virtualFaxRequired}
                    onChange={(e) => setFaxDraft(row.orgId, { virtualFaxRequired: e.target.checked })}
                    disabled={savingFax}
                  />
                  <span>가상팩스 필수</span>
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-slate-700">메모</span>
                  <input
                    className="mt-1 w-full rounded border px-3 py-2"
                    value={faxDraft.memo}
                    onChange={(e) => setFaxDraft(row.orgId, { memo: e.target.value })}
                    disabled={savingFax}
                  />
                </label>
                <button
                  type="button"
                  className="rounded bg-brand-navy px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  onClick={() => saveFax(row.orgId)}
                  disabled={savingFax}
                >
                  {savingFax ? '저장 중…' : '청구번호 저장'}
                </button>
              </>
            ) : (
              <OrgRequired />
            )}
          </div>

          <div className="space-y-3 rounded border p-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="font-semibold">전산 관련</div>
              <SystemSourceBadge row={row} />
            </div>
            {!row.orgId ? (
              <OrgRequired />
            ) : (
              <div className="space-y-3 text-sm">
                <div className="grid gap-2 sm:grid-cols-[120px_1fr]">
                  <select
                    className="rounded border px-3 py-2"
                    value={systemValue(row, 'sectionId')}
                    onChange={(e) => {
                      const sectionId = e.target.value
                      const sectionTitle = sectionId === 'life' ? '생명보험사' : sectionId === 'mutual' ? '공제회사' : '손해보험사'
                      const firstColLabel = sectionId === 'mutual' ? '공제회사 홈페이지' : '보험회사 전산'
                      setSystemDraft(row.id, { sectionId, sectionTitle, firstColLabel })
                    }}
                    disabled={savingSystem}
                  >
                    <option value="fire">손해</option>
                    <option value="life">생명</option>
                    <option value="mutual">공제</option>
                  </select>
                  <input
                    className="rounded border px-3 py-2"
                    value={systemValue(row, 'sectionTitle')}
                    onChange={(e) => setSystemDraft(row.id, { sectionTitle: e.target.value })}
                    disabled={savingSystem}
                  />
                </div>
                {[
                  ['systemUrl', '전산 URL'],
                  ['termsUrl', '약관 URL'],
                  ['formUrl', '서식 URL'],
                  ['logoUrl', '외부 로고 URL'],
                ].map(([key, label]) => (
                  <label key={key} className="block">
                    <span className="font-medium text-slate-700">{label}</span>
                    <input
                      className="mt-1 w-full rounded border px-3 py-2"
                      value={systemValue(row, key)}
                      onChange={(e) => setSystemDraft(row.id, { [key]: e.target.value })}
                      disabled={savingSystem}
                    />
                  </label>
                ))}
                {[
                  ['customerCenterText', '고객센터'],
                  ['inCallText', '인콜 모니터링'],
                  ['helpDeskText', '전산 헬프데스크'],
                  ['claimFaxText', '전산표 청구팩스'],
                ].map(([key, label]) => (
                  <label key={key} className="block">
                    <span className="font-medium text-slate-700">{label}</span>
                    <textarea
                      className="mt-1 h-20 w-full rounded border px-3 py-2"
                      value={systemValue(row, key)}
                      onChange={(e) => setSystemDraft(row.id, { [key]: e.target.value })}
                      disabled={savingSystem}
                    />
                  </label>
                ))}
                {systemHref ? <div className="text-xs text-slate-500">전화 링크: {systemHref}</div> : null}
                <div className="flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={!!systemValue(row, 'active')}
                      onChange={(e) => setSystemDraft(row.id, { active: e.target.checked })}
                      disabled={savingSystem}
                    />
                    <span>보험사전산 화면 노출</span>
                  </label>
                  <button
                    type="button"
                    className="rounded bg-brand-navy px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    onClick={() => saveSystemProfile(row)}
                    disabled={savingSystem}
                  >
                    {savingSystem ? '저장 중…' : row.systemProfile ? '전산 정보 저장' : '정적값 DB 저장'}
                  </button>
                  {row.systemProfile ? (
                    <button
                      type="button"
                      className="text-sm font-medium text-rose-700 underline disabled:opacity-50"
                      onClick={() => deleteSystemProfile(row)}
                      disabled={savingSystem}
                    >
                      DB 관리 해제
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
